import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack, Stepper, Step, StepLabel, CardActions, Collapse, Container
} from '@mui/material';
import { TrueffortButton, TrueffortButtonSmall, TrueffortChip } from '../MUIstyle/MUICustom';
import { useHistory } from 'react-router-dom';
import SiteWrapper from '../SiteWrapper.react';
import { toast } from 'react-toastify';
import { ExpandMore as ExpandMoreICon, ExpandLess as ExpandLessICon } from '@mui/icons-material';
import Grid from '@mui/material/Unstable_Grid2';
import ProjectService from "../services/project.service";
import ApiInfoForm from "../components/jira/ApiInfoForm";
import APISelect from "../components/jira/APISelect";
import InteroperabilityService from '../services/interoperabilty.service';
import { TOOLS_EXTERNAL_NAME } from '../util/Constants';

const steps: string[] = ['select_tool', 'submit_info'];

function ProjectsExternalAPI() {
  const [organizationDomain, setOrganizationDomain] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [projects, setProjects] = useState([]);
  const [trueffortProjects, setTrueffortProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false); // Nuevo estado para controlar la carga
  const { t } = useTranslation();
  const history = useHistory();
  const projectService = useMemo(() => new ProjectService());
  const interoperabilityService = useMemo(() => new InteroperabilityService());
  const [showMore, setShowMore] = useState(true);
  const [selectedAPI, setSelectedAPI] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  const [errors, setErrors] = useState({
    organizationDomain: '',
    email: '',
    token: ''
  });

  React.useEffect(() => {
    // Una vez que se llenaron los proyectos de JIRA, consultar los proyectos de trueffort, para determinar cuáles ya están guardados
    getTrueffortProjects();
  }, [projects]);

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <APISelect handleToolSelect={handleToolSelection} />
        );

      case 1:
        return (
          <ApiInfoForm
            t={t}
            selectedAPI={selectedAPI}
            organizationDomainState={{
              organizationDomain: organizationDomain,
              setOrganizationDomain: setOrganizationDomain
            }}
            emailState={{
              email: email,
              setEmail: setEmail
            }}
            tokenState={{
              token: token,
              setToken: setToken
            }}
            errors={errors}
          />
        );

      default:
        throw new Error('Unknown step');
    }
  }

  const getParams = () => {
    interoperabilityService.getInfoConnection(selectedAPI).then(({data:response}:any) => {
      if (response.success) {
        const {data} = response;
        let allParams: boolean = true;

        if (selectedAPI == TOOLS_EXTERNAL_NAME.jira) {
          if (data.email) {
            setEmail(data.email);
          } else {
            allParams = false;
          }
        }
        setOrganizationDomain(data.dominio);
        setToken(data.token);

        if (allParams) {
          setLoading(true);
          interoperabilityService.getProjects(data.dominio, data.token, selectedAPI, data?.email).then(({ data: response }: any) => {
            const {data} = response;
            //console.log(data);
            //let dataJ = JSON.parse(data);
            if (data.length === 0) {
              toast.warning(t('Herramienta.no_projects_found')); // Mostrar toast si no se encontraron proyectos
            } else {
              //console.log("JIRA P", dataJ);
              setProjects(data);
              setLoaded(true); // Marcamos que los proyectos se han cargado
              setShowMore(false);
            }
          }).catch((error:any) => toast.error(t('Herramienta.connection_error')))
          .finally(() => setLoading(false));
        }
      } else {
        organizationDomain !== '' && setOrganizationDomain('');
        email !== '' && setEmail('');
        token !== '' && setToken('');
        projects.length > 0 && setProjects([]);
        setLoaded(false);
        //setShowMore(true);
      }
    });
  }


  // Define una función asíncrona handleGetProjects que valida los campos, muestra un indicador de carga, intenta
  // obtener los proyectos de Jira usando el servicio infoJiraProjectsService, actualiza el estado de projects y loaded,
  // y finaliza la carga independientemente del resultado
  const handleGetProjects = async () => {
    const isValid = validateFields();

    if (isValid) {
      setLoading(true); // Indicar que se están cargando los proyectos
      try {
        let response = await interoperabilityService.getProjects(organizationDomain, token, selectedAPI, email);
        if (response?.data?.success) {
          setProjects(response?.data?.data);
        }
        setLoaded(true); // Marcamos que los proyectos se han cargado
        if (response?.data?.data?.length === 0) {
          toast.error(t('Herramienta.no_projects_found')); // Mostrar toast si no se encontraron proyectos
        } else {
          setShowMore(false);
        }
      } catch (error) {
        toast.error(t('Herramienta.connection_error')); // Agregamos el toast de error
      } finally {
        setLoading(false); // Finaliza la carga, independientemente del resultado
      }
    }
  };

  const getTrueffortProjects = () => {
    // @ts-ignore
    projectService.getProjectsInteroperabilityInfo().then(async ({ data: response }) => {
      if (Array.isArray(response.data)) {
        //console.log("TRUEFFORT PROJECTS", response.data);
        setTrueffortProjects(response.data);
      }
    });
  }

  const isProjectInTrueffort = (idProjectJira: string) => {
    //console.log("isProjectInTrueffort: " + idProjectJira);
    //console.log(trueffortProjects);
    if (trueffortProjects.length > 0 && typeof idProjectJira != "undefined") {
      let exists = trueffortProjects.find((e: any) => e.idProyectoHerramienta === idProjectJira);
      return typeof exists != "undefined";
    } else {
      return false;
    }
  }

  const getIdProjectInTrueffort = (idProjectJira: string) => {
    //console.log("isProjectInTrueffort: " + idProjectJira);
    if (trueffortProjects.length > 0 && typeof idProjectJira != "undefined") {
      let exists = trueffortProjects.find((e: any) => e.idProyectoHerramienta === idProjectJira);
      if (typeof exists != "undefined") {
        return exists.idProject;
      } else {
        return -1;
      }
    } else {
      return -1;
    }
  }

  // Define una función handleSelectProject que toma un projectId y un projectName, y navega a la 
  // ruta /jiraProjects pasando estos valores junto con organizationDomain, email y token como estado para usarlos en la siguiente petición al back
  const handleSelectProject = (projectId: any, projectName: string, trueffortId: number) => {
    console.log(projectId);
    console.log(trueffortId);
    // Navegar a la ruta /jiraProjects o /azureProjects, según sea el caso, y pasar el projectId como parámetro
    history.push('/apiProjectDetails', { selectedAPI, projectId, projectName, organizationDomain, email, token, trueffortId });
  };

  const validateFields = () => {
    let isValid = true;
    const newErrors = {
      organizationDomain: '',
      email: '',
      token: ''
    };

    if (!organizationDomain.trim()) {
      isValid = false;
      newErrors.organizationDomain = t("Herramienta.no_domain");
    }

    if (selectedAPI === TOOLS_EXTERNAL_NAME.jira) {
      if (!email.trim()) {
        isValid = false;
        newErrors.email = t("Herramienta.no_email");
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        isValid = false;
        newErrors.email = t("Herramienta.invalid_email");
      }
    }
    

    if (!token.trim()) {
      isValid = false;
      newErrors.token = t("Herramienta.no_token");
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleToolSelection = (selectedTool: number) => {
    setActiveStep(activeStep + 1);
    setSelectedAPI(selectedTool);
  }

  useEffect(() => {
    if (selectedAPI) {
      getParams();
    }
  }, [selectedAPI]);

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    setProjects([]);
    setTrueffortProjects([]);
  }

  return (
    <SiteWrapper>
      <Box className="my-3 my-md-5">
        <Container>
          <Stack spacing={1} direction='row' justifyContent="space-between" alignItems="center" >
            <h1 className="page-title m-4">
              {t('Herramienta.interoperability')}
            </h1>
          </Stack>

          <Card elevation={2}>
            <CardContent>
              <Collapse
                in={showMore}
                timeout="auto"
                unmountOnExit
                sx={{ width: "100%" }}
              >
                <Stack direction="column" spacing={5} >
                  <Stepper activeStep={projects.length > 0 ? 2 : activeStep} alternativeLabel>
                    {steps.map((label: string) =>
                      <Step key={label} >
                        <StepLabel>{t('Herramienta.' + label)}</StepLabel>
                      </Step>
                    )}
                  </Stepper>

                  {getStepContent(activeStep)}

                  {activeStep !== 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column-reverse', sm: 'row' },
                        justifyContent: 'space-evenly'
                      }}
                    >
                      <TrueffortButton
                        //startIcon={<ChevronLeftRoundedIcon />}
                        onClick={handleBack}
                        variant="text"
                      /*sx={{
                          display: { xs: 'none', sm: 'flex' },
                      }}*/
                      >
                        {t('Herramienta.back')}
                      </TrueffortButton>

                      <TrueffortButton
                        variant="contained"
                        color="primary"
                        //endIcon={<ChevronRightRoundedIcon />}
                        onClick={handleGetProjects}
                      >
                        {t('Herramienta.get')}
                      </TrueffortButton>
                    </Box>
                  )}
                </Stack>
              </Collapse>
            </CardContent>

            <CardActions sx={{ display: 'flex', justifyContent: 'center' }} >
              {(activeStep !== 0 && projects.length > 0) &&
                <TrueffortButtonSmall
                  id="idBtnCollapse"
                  color="secondary"
                  variant="contained"
                  size="small"
                  onClick={() => setShowMore(!showMore)}
                >
                  {showMore ?
                    <ExpandLessICon sx={{ fontSize: ".9rem" }} /> :
                    <ExpandMoreICon sx={{ fontSize: ".9rem" }} />
                  }
                  {t("Herramienta.edit_params")}
                </TrueffortButtonSmall>
              }
            </CardActions>
          </Card>
        </Container>
        <Box className="my-3 my-md-5">
          <Container>
            <Box mt={3} ml={2}>
              <h1 className="page-title m-4">
                {t('Herramienta.projects')}
              </h1>
              {loading && ( /* Si loading es verdadero, renderiza lo siguiente */
                <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '200px' }}>
                  <CircularProgress /> {/* Muestra un CircularProgress (indicador de carga) */}
                </Grid>
              )}
              {!loading && loaded && (
                projects.map((project: any) => (  /* Mapea el arreglo projects y renderiza lo siguiente por cada proyecto */
                  <Card
                    key={project.id} /* Utiliza el id del proyecto como clave única */
                    sx={{
                      border: "solid thin #64A8EC",
                      borderRadius: "10px",
                      marginTop: "12px",
                      padding: "12px",
                      borderLeft: " solid 8px #64A8EC",
                    }}
                  >
                    <Grid container>
                      <Grid xs={8}>
                        <span className="home-project-detail-label">{t('project_tasks_page.title')}</span>
                        <h5>{project.name}</h5> {/* Muestra el nombre del proyecto */}
                      </Grid>
                      <Grid xs={2} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleSelectProject(project.id, project.name, getIdProjectInTrueffort(project.id))} /* Llama a handleSelectProject con el id y nombre del proyecto al hacer click */
                        >
                          {t('Herramienta.select_project')}
                        </Button>
                      </Grid>

                      <Grid xs={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }} >
                        {isProjectInTrueffort(project.id) &&
                          <TrueffortChip color="green" variant="outlined" size="small" label={"EN TRUEFFORT"} />
                        }
                      </Grid>
                    </Grid>
                  </Card>
                ))
              )}
              {!loading && !loaded && ( /* Si loading y loaded son falsos, renderiza lo siguiente */
                <Card sx={{ border: "solid thin #64A8EC", borderRadius: "10px", marginTop: "12px", padding: "12px", borderLeft: " solid 8px #64A8EC" }}>
                  <Grid container>
                    <Grid xs={12} sx={{ textAlign: "center" }}>
                      <span className="home-project-detail-label">{t('Herramienta.no_projects')}...</span>
                    </Grid>
                  </Grid>
                </Card>
              )}
            </Box>

          </Container>
        </Box>
      </Box>
    </SiteWrapper>
  );
}

export default ProjectsExternalAPI;
